import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { registerLockModal, markUnlocked } from "@/lib/stockLock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Lock, ShieldCheck } from "lucide-react";

/**
 * Global modal that opens whenever a stock-modifying action needs to be
 * authorised by the user's PIN. Rendered once at the App root.
 */
export default function StockLockModal() {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const resolveRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    registerLockModal(
      () =>
        new Promise((resolve) => {
          resolveRef.current = resolve;
          setPin("");
          setErr("");
          setOpen(true);
        }),
    );
  }, []);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  const finish = (ok) => {
    setOpen(false);
    setPin("");
    setErr("");
    resolveRef.current?.(ok);
    resolveRef.current = null;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/verify-pin", { pin: pin.trim() });
      markUnlocked();
      toast.success("Akses diberikan");
      finish(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "PIN salah");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) finish(false);
      }}
    >
      <DialogContent className="max-w-sm" data-testid="stock-lock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" /> PIN Diperlukan
          </DialogTitle>
          <DialogDescription>
            Aksi ini mengubah data stok. Masukkan PIN untuk melanjutkan (sekali per sesi).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="current-password"
            placeholder="Masukkan PIN"
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              if (err) setErr("");
            }}
            data-testid="stock-lock-input"
            className="h-11 text-base tracking-widest text-center"
          />
          {err && (
            <div className="text-xs text-rose-600" data-testid="stock-lock-error">
              {err}
            </div>
          )}
          <DialogFooter className="pt-1">
            <Button type="button" variant="outline" onClick={() => finish(false)}>
              Batal
            </Button>
            <Button
              type="submit"
              disabled={loading || !pin.trim()}
              data-testid="stock-lock-submit"
              className="bg-zinc-900 hover:bg-zinc-800"
            >
              <ShieldCheck className="h-4 w-4 mr-1.5" />
              {loading ? "Memverifikasi…" : "Buka Akses"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
